// Hand-picked vibrant-but-not-garish gradient pairs for kid mode. Each surah
// gets a deterministic gradient based on its number, so the same surah always
// looks the same and the home page reads as a varied, colorful collection
// rather than a wall of one accent. Twelve pairs covers Juz' Amma + Al-Fatiha
// (38 surahs) with enough variety that adjacent tiles never share a color.
//
// Picked to:
//   1. Maintain WCAG AA contrast against white text (4.5:1+ on the darker stop)
//   2. Stay readable in both light and dark themes
//   3. Avoid red-only tiles that could feel "wrong/alert" to a child
export const KID_GRADIENTS: Array<readonly [string, string]> = [
  ['#FF6B9D', '#C44569'], // raspberry
  ['#48C9B0', '#16A085'], // mint
  ['#A569BD', '#6C3483'], // grape
  ['#FFA63E', '#FF6B6B'], // sunset
  ['#52BE80', '#229954'], // forest
  ['#5DADE2', '#2874A6'], // ocean
  ['#F4D03F', '#E67E22'], // mango
  ['#76D7C4', '#117864'], // teal
  ['#EC7063', '#C0392B'], // coral
  ['#5499C7', '#1F618D'], // sapphire
  ['#AF7AC5', '#7D3C98'], // amethyst
  ['#F1948A', '#922B21'], // rose
]

// Muted grey gradient for locked lessons — distinct enough from any vivid
// tile that "locked" reads at a glance, but not so dark that the lock icon
// loses contrast. Same in both themes.
const LOCKED_GRADIENT: readonly [string, string] = ['#BDC3C7', '#7F8C8D']

/**
 * Deterministic per-surah gradient. Subtracting 1 so surah 1 (Al-Fatiha)
 * gets the first color in the palette and the sequence stays stable across
 * any future palette reorderings.
 */
export function gradientForSurah(surahNumber: number, locked = false): readonly [string, string] {
  if (locked) return LOCKED_GRADIENT
  return KID_GRADIENTS[(Math.max(1, surahNumber) - 1) % KID_GRADIENTS.length]
}

/** Convenience: turn a [from, to] pair into a CSS linear-gradient string. */
export function gradientCss(g: readonly [string, string], angle = 135): string {
  return `linear-gradient(${angle}deg, ${g[0]} 0%, ${g[1]} 100%)`
}

/**
 * Hero card uses the teal brand pair to unify with the adult app's white-first
 * system. Brighter than surah tiles to stand out; picked once (not deterministic
 * per user) — feels more like a brand element.
 */
export const HERO_GRADIENT: readonly [string, string] = ['#14B8A6', '#0F766E']
